/**
 * THE component display-name resolver — every UI label for a needle component
 * resolves here (inspector groups, eventlist retarget dropdown, search).
 * Engine-import-free (chrome bundle): the engine's symbols are matched by
 * DESCRIPTION, never imported. For DOCUMENT type names (must be
 * TypeStore-loadable) use `defComponentTypeName` instead.
 */

/** glb loader / AddComponentCommand stamp — set on INSTANCES, file-authored */
const STAMP_DESCRIPTION = "original-component-name";
/** Component.prototype getter → TypeStore registry key */
const GETTER_DESCRIPTION = "component-name";

export function resolveComponentName(obj: object | null | undefined): string | null {
    if (!obj || typeof obj !== "object") return null;
    const record = obj as Record<string | symbol, unknown>;

    // Walk the INSTANCE first, then the prototype chain — the loader stamps
    // instances, so a prototype-only walk missed every stamped component and
    // fell through to minified constructor names.
    for (let level: object | null = obj; level; level = Object.getPrototypeOf(level)) {
        let getterValue: string | null = null;
        for (const sym of Object.getOwnPropertySymbols(level)) {
            const description = sym.description;
            if (description !== STAMP_DESCRIPTION && description !== GETTER_DESCRIPTION) continue;
            // read through the instance so prototype getters see `this`
            const value = record[sym];
            if (typeof value !== "string" || value.length === 0) continue;
            if (description === STAMP_DESCRIPTION) return value;
            getterValue ??= value;
        }
        if (getterValue) return getterValue;
        const ctor = (level as { constructor?: { NAME?: unknown } }).constructor;
        if (typeof ctor?.NAME === "string" && ctor.NAME.length > 0) return ctor.NAME;
    }

    const tagged = record["needle-component-name"];
    if (typeof tagged === "string" && tagged.length > 0) return tagged;
    // pre-4.5 engines
    const legacy = record["__name"];
    if (typeof legacy === "string" && legacy.length > 0) return legacy;

    const ctorName = (obj as { constructor?: { name?: string } }).constructor?.name;
    if (ctorName && ctorName !== "Object") {
        let name = ctorName;
        while (name.startsWith("_")) name = name.substring(1);
        name = name.replace(/[0-9]+$/, "");
        // 1-3 characters = minified junk, worse than no name
        if (name.length > 3) return name;
    }

    return null;
}

/** UI convenience: never shows minified junk, degrades to a generic label. */
export function displayComponentName(obj: object | null | undefined): string {
    return resolveComponentName(obj) ?? "Component";
}
